import React, { useMemo } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useOrg } from '../../contexts/OrgContext';
import { useNightPolicy, useMovementBuckets } from '../../lib/movementView';
import { StatusLight } from './StatusLight';
import { DayBar, DayBarLegend } from './DayBar';
import { NightDots } from './NightDots';

const Kicker = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-3">{children}</div>
);

// Family-friendly movement card: status light, last 24 h bar, last 7 nights.
// scope = { patient_id } or { room_id }; devices are already privacy-filtered by the caller.
export function MovementOverview({ devices, name, kind = 'patient', scope, orgId, bare = false, testid = 'movement-overview' }) {
  const { t } = useI18n();
  const { current } = useOrg() || {};
  const policy = useNightPolicy(orgId || current?.id);
  const ids = useMemo(() => devices.map((d) => d.device_uuid), [devices]);
  const buckets = useMovementBuckets({ days: 8, ...scope }, ids.length > 0);

  const body = (
    <div className="space-y-7" data-testid={testid}>
      <StatusLight devices={devices} name={name} kind={kind} policy={policy} testid={`${testid}-status`} />
      <div>
        <Kicker>{t('mvd_title')}</Kicker>
        {buckets && policy ? <DayBar buckets={buckets} ids={ids} policy={policy} testid={`${testid}-daybar`} /> : <div className="h-9 rounded-md bg-bg-alt animate-pulse" />}
        <div className="mt-2"><DayBarLegend /></div>
      </div>
      <div>
        <Kicker>{t('mvn_title')}</Kicker>
        {buckets && policy ? <NightDots buckets={buckets} ids={ids} policy={policy} testid={`${testid}-nights`} /> : <div className="h-10 rounded-md bg-bg-alt animate-pulse max-w-md" />}
      </div>
    </div>
  );
  if (bare) return body;
  return (
    <section className="bg-white rounded-2xl border border-stone_line p-6 sm:p-8 shadow-soft">
      <h2 className="font-heading text-2xl text-ink mb-6">{t('mvo_title')}</h2>
      {body}
    </section>
  );
}
